import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';

const FeedbackListItem = ({ feedback, isSelected, onClick, isMobile }) => {
  const userData = feedback.userData || {};
  const name = userData.displayName || userData.email?.split('@')[0] || 'User';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const timeAgo = formatDistanceToNow(new Date(feedback.date), { addSuffix: true });

  const statusConfig = {
    new: { color: 'bg-emerald-500', label: 'New', textColor: 'text-emerald-700 dark:text-emerald-400' },
    assigned: { color: 'bg-blue-500', label: 'Assigned', textColor: 'text-blue-700 dark:text-blue-400' },
    in_progress: { color: 'bg-amber-500', label: 'In Progress', textColor: 'text-amber-700 dark:text-amber-400' },
    resolved: { color: 'bg-gray-400', label: 'Resolved', textColor: 'text-gray-700 dark:text-gray-400' },
    wont_fix: { color: 'bg-red-500', label: 'Won\'t Fix', textColor: 'text-red-700 dark:text-red-400' }
  };

  const currentStatus = statusConfig[feedback.status] || statusConfig.new;

  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Low' },
    medium: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Medium' },
    high: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', label: 'High' },
    critical: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Critical' }
  };

  const priority = priorityConfig[feedback.priority] || priorityConfig.low;

  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      className={cn(
        'w-full group transition-all duration-200',
        'border-l-4',
        isSelected
          ? 'bg-accent/80 border-l-primary shadow-sm'
          : 'hover:bg-accent/40 border-l-transparent',
        'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1',
        isMobile ? 'min-h-[72px]' : 'min-h-[64px]'
      )}
      type="button"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Avatar */}
        <Avatar className={cn(
          "flex-shrink-0",
          isMobile ? "h-10 w-10" : "h-8 w-8"
        )}>
          {userData.photoURL ? (
            <img
              src={userData.photoURL}
              alt={name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <AvatarFallback className={cn(
              "font-medium bg-muted text-muted-foreground",
              isMobile ? "text-sm" : "text-xs"
            )}>
              {initials}
            </AvatarFallback>
          )}
        </Avatar>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={cn(
                  "font-medium text-foreground truncate",
                  isMobile ? "text-sm" : "text-sm"
                )}>
                  {name}
                </span>
                <span className="text-base opacity-70">{feedback.emoji}</span>
                {feedback.priority && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-xs px-1.5 py-0.5",
                      priority.color
                    )}
                  >
                    {priority.label}
                  </Badge>
                )}
              </div>
              <p className={cn(
                "text-muted-foreground line-clamp-2 leading-relaxed",
                isMobile ? "text-sm" : "text-xs"
              )}>
                {feedback.comment}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
              <div className="flex items-center gap-1.5">
                <div className={cn('w-2 h-2 rounded-full', currentStatus.color)} />
                <span className={cn(
                  "text-muted-foreground",
                  isMobile ? "text-xs" : "text-xs"
                )}>
                  {timeAgo}
                </span>
              </div>
              {feedback.upvoteCount > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <span>👍</span>
                  <span>{feedback.upvoteCount}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.button>
  );
};

export default FeedbackListItem;